import { ApiErrorCode, PASSWORD_MIN_LENGTH, type AccountView } from '@bioforge/protocol';
import { ApiRequestError, type ApiClient } from '../api/api-client.js';
import { button, overlayRoot } from './html.js';

export type AccountFormMode = 'register' | 'login';

const ERROR_TEXT: Partial<Record<ApiErrorCode, string>> = {
  [ApiErrorCode.EmailTaken]: 'Эта почта уже зарегистрирована. Войдите в аккаунт.',
  [ApiErrorCode.InvalidCredentials]: 'Неверная почта или пароль.',
  [ApiErrorCode.InvalidRequest]: `Проверьте поля: почта, пароль от ${PASSWORD_MIN_LENGTH} символов, имя до 20 символов.`,
  [ApiErrorCode.RateLimited]: 'Слишком много попыток. Подождите минуту.',
  [ApiErrorCode.AlreadyRegistered]: 'Аккаунт уже зарегистрирован.',
};

/** Текст ошибки формы для игрока. */
export function accountErrorText(error: unknown): string {
  if (error instanceof ApiRequestError && error.code) {
    return ERROR_TEXT[error.code] ?? 'Не получилось. Попробуйте ещё раз.';
  }
  return 'Сервер недоступен. Попробуйте ещё раз.';
}

/**
 * Форма регистрации или входа — HTML поверх холста (SPEC.md, раздел 3): нужны настоящие
 * поля ввода, автозаполнение паролей и экранная клавиатура на телефоне.
 */
export class AccountOverlay {
  private readonly root = overlayRoot();

  constructor(
    api: ApiClient,
    mode: AccountFormMode,
    private readonly onDone: (account: AccountView | null) => void,
    /** Переход между регистрацией и входом (для гостя). */
    onSwitch?: (mode: AccountFormMode) => void,
  ) {
    const guest = api.current.kind === 'guest';
    const form = document.createElement('form');
    Object.assign(form.style, {
      width: 'min(380px, calc(100vw - 32px))',
      boxSizing: 'border-box',
      padding: '24px',
      borderRadius: '18px',
      background: '#0e2233',
      border: '3px solid rgba(158,220,240,.45)',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    });
    const title = document.createElement('h2');
    title.textContent = mode === 'register' ? 'Регистрация' : 'Вход';
    title.style.margin = '0';
    const hint = document.createElement('p');
    hint.style.margin = '0';
    hint.style.color = '#9fc4d6';
    hint.textContent =
      mode === 'register'
        ? 'Весь прогресс гостя сохранится в аккаунте и будет доступен на любом устройстве.'
        : guest
          ? 'Прогресс текущего гостя останется в гостевом аккаунте и будет недоступен после входа.'
          : 'Войдите в другой аккаунт.';

    const email = field('email', 'Почта', 'email');
    const password = field(
      'password',
      'Пароль',
      mode === 'register' ? 'new-password' : 'current-password',
    );
    password.input.minLength = mode === 'register' ? PASSWORD_MIN_LENGTH : 1;
    const name = field('text', 'Имя игрока', 'username');
    name.input.maxLength = 20;
    name.input.value = api.current.name;

    const error = document.createElement('p');
    error.setAttribute('role', 'alert');
    Object.assign(error.style, { margin: '0', color: '#ffb3c8', minHeight: '1.2em' });

    const buttons = document.createElement('div');
    Object.assign(buttons.style, { display: 'flex', gap: '12px', justifyContent: 'flex-end' });
    const cancel = button('Отмена', '#24506b');
    cancel.type = 'button';
    cancel.addEventListener('click', () => this.close(null));
    const submit = button(mode === 'register' ? 'Зарегистрироваться' : 'Войти', '#1b7d74');
    submit.type = 'submit';
    buttons.append(cancel, submit);

    form.append(title, hint, email.label, password.label);
    if (mode === 'register') form.append(name.label);
    form.append(error, buttons);
    if (guest && onSwitch) {
      const other: AccountFormMode = mode === 'register' ? 'login' : 'register';
      const link = button(
        mode === 'register' ? 'Уже есть аккаунт? Войти' : 'Нет аккаунта? Зарегистрироваться',
        'transparent',
      );
      link.style.color = '#7ff5e6';
      link.addEventListener('click', () => {
        this.destroy();
        onSwitch(other);
      });
      form.append(link);
    }
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      submit.disabled = true;
      error.textContent = '';
      const request =
        mode === 'register'
          ? api.register({
              email: email.input.value,
              password: password.input.value,
              name: name.input.value,
            })
          : api.login({ email: email.input.value, password: password.input.value });
      request.then(
        (account) => this.close(account),
        (failure: unknown) => {
          error.textContent = accountErrorText(failure);
          submit.disabled = false;
        },
      );
    });
    this.root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close(null);
    });

    this.root.append(form);
    document.body.appendChild(this.root);
    email.input.focus();
  }

  private close(account: AccountView | null): void {
    this.root.remove();
    this.onDone(account);
  }

  /** Убирает форму без результата (экран закрывается). */
  destroy(): void {
    this.root.remove();
  }
}

function field(
  type: string,
  caption: string,
  autocomplete: AutoFill,
): { label: HTMLLabelElement; input: HTMLInputElement } {
  const label = document.createElement('label');
  Object.assign(label.style, { display: 'flex', flexDirection: 'column', gap: '4px' });
  label.textContent = caption;
  const input = document.createElement('input');
  input.type = type;
  input.required = true;
  input.autocomplete = autocomplete;
  Object.assign(input.style, {
    boxSizing: 'border-box',
    height: '44px',
    padding: '0 12px',
    borderRadius: '10px',
    border: '2px solid rgba(158,220,240,.45)',
    background: '#0a1826',
    color: '#f2fbff',
    font: '16px system-ui, sans-serif',
  });
  label.append(input);
  return { label, input };
}
